import { useState, useEffect } from 'react';
import NicknameBar from '../components/NicknameBar';

interface BondOptionResult {
  id: string;
  text: string;
  resolvedText: string;
  votes: number;
  percentage: number;
}

interface BondResultsScreenProps {
  voteId: string;
}

function BondResultsScreen({ voteId }: BondResultsScreenProps) {
  const [options, setOptions] = useState<BondOptionResult[]>([]);
  const [winningOptionId, setWinningOptionId] = useState<string | null>(null);
  const [heroName, setHeroName] = useState<string>('');

  // Fetch results
  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`/api/bond-votes/${voteId}/results`);
        if (response.ok) {
          const data = await response.json();
          setOptions(data.options || []);
          setWinningOptionId(data.winningOptionId);
        }
      } catch (error) {
        console.error('[BondResultsScreen] Error fetching results:', error);
      }
    };

    fetchResults();
    // Poll for updates
    const interval = setInterval(fetchResults, 2000);
    return () => clearInterval(interval);
  }, [voteId]);

  // Fetch hero name
  useEffect(() => {
    const fetchHeroInfo = async () => {
      try {
        const response = await fetch(`/api/bond-votes/${voteId}`);
        if (response.ok) {
          const data = await response.json();
          const sessionId = data.bondVote.sessionId;
          const fromTeam = data.bondVote.fromTeam;

          // Get the winning sheet from sheet votes
          const votesResponse = await fetch(`/api/sheet-votes/session/${sessionId}/latest`);
          if (votesResponse.ok) {
            const votesData = await votesResponse.json();
            const teamVote = votesData.votes?.find((v: any) => v.teamColor === fromTeam && v.finalSheetId);
            if (teamVote?.winnerSheet?.name) {
              setHeroName(teamVote.winnerSheet.name);
            }
          }
        }
      } catch (error) {
        console.error('[BondResultsScreen] Error fetching hero info:', error);
      }
    };

    fetchHeroInfo();
  }, [voteId]);

  return (
    <div className="flex flex-col min-h-screen p-4 pt-10">
      <NicknameBar />
      {/* Header */}
      <div className="text-center py-4">
        <h1 className="text-2xl font-heading font-bold text-ink mb-1">Risultati Legame</h1>
        {heroName && (
          <p className="text-xl text-team-yellow font-semibold">{heroName}</p>
        )}
      </div>

      {/* Results */}
      <div className="flex-1 flex flex-col space-y-3 max-w-md mx-auto w-full pt-2">
        {options.map((option) => {
          const isWinner = option.id === winningOptionId;

          return (
            <div
              key={option.id}
              className={`rounded-xl p-4 border-2 ${
                isWinner
                  ? 'bg-team-yellow-bg border-team-yellow'
                  : 'parchment-card border-parchment-300'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className={`text-lg font-semibold ${isWinner ? 'text-team-yellow' : 'text-ink'}`}>
                  {isWinner && '🏆 '}{option.resolvedText}
                </p>
                <span className={`text-xl font-bold ${isWinner ? 'text-team-yellow' : 'text-ink'}`}>
                  {option.percentage}%
                </span>
              </div>
              <div className="h-3 bg-parchment-300 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    isWinner ? 'bg-team-yellow' : 'bg-parchment-400'
                  }`}
                  style={{ width: `${option.percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BondResultsScreen;
